// components/table-with-column-manager/table-with-column-manager.tsx
import React, { useMemo } from 'react'
import { Table, Form } from 'antd'
import type { TableProps } from 'antd'
import type { ColumnsType, ColumnGroupType } from 'antd/es/table'
import styled from 'styled-components'
import { ColumnManager } from '../column-manager/column-manager'
import { useColumnManager } from '../../hooks/use-collum-manager'
import { generateSummaryRow } from './summary-generator'
import type { FilterConfig } from './types'

const TableWrapper = styled.div`
    display: flex;
    flex-direction: column;
`

const ColumnManagerWrapper = styled.div`
    margin-bottom: 16px;
`

interface TableWithColumnManagerProps extends Omit<TableProps<any>, 'summary'> {
    storageKey?: string
    defaultVisibleColumns?: string[]
    /** Массив фильтров для столбцов */
    filters?: FilterConfig[]
    /** Содержимое ячейки действий (обычно кнопка "Сброс"), размещается в последнем столбце */
    actionsCell?: React.ReactNode
    /** CSS класс для ячейки действий */
    actionsCellClassName?: string
    /** Form instance для фильтров (опционально, если не передан, создается внутренний) */
    filterForm?: ReturnType<typeof Form.useForm>[0]
    /** Callback для обработки изменений в фильтрах */
    onFilterChange?: () => void
}

export const TableWithColumnManager: React.FC<TableWithColumnManagerProps> = ({
    columns = [],
    storageKey = 'table-columns',
    defaultVisibleColumns,
    filters = [],
    actionsCell,
    actionsCellClassName,
    filterForm,
    onFilterChange,
    ...tableProps
}) => {
    const [internalForm] = Form.useForm()
    const form = filterForm || internalForm

    const {
        visibleColumns,
        toggleColumn,
        showAllColumns,
        hideAllColumns,
        resetToDefault,
        columnSettings,
    } = useColumnManager({
        allColumns: columns,
        storageKey,
        defaultVisible: defaultVisibleColumns,
    })

    const leadingColumnCount = useMemo(() => {
        let count = 0

        if (tableProps.rowSelection) {
            count += 1
        }

        const expandableConfig = tableProps.expandable
        const hasExpandableColumn =
            typeof tableProps.expandedRowRender === 'function' ||
            (expandableConfig && typeof expandableConfig.expandedRowRender === 'function')

        if (hasExpandableColumn) {
            count += 1
        }

        return count
    }, [tableProps.rowSelection, tableProps.expandable, tableProps.expandedRowRender])

    const flattenedVisibleColumns = useMemo(() => {
        return flattenLeafColumns(visibleColumns)
    }, [visibleColumns])

    // Генерируем summary, если есть фильтры
    const generatedSummary = useMemo(() => {
        // Если нет фильтров, не генерируем summary
        if (!filters || filters.length === 0) {
            return undefined
        }

        return () => (
            <Table.Summary fixed={'top'}>
                <Form
                    form={form}
                    component={false}
                    variant={'filled'}
                    onFieldsChange={() => {
                        onFilterChange?.()
                    }}
                >
                    {generateSummaryRow(flattenedVisibleColumns, filters, {
                        leadingColumnCount,
                        actionsCell,
                        actionsCellClassName,
                    })}
                </Form>
            </Table.Summary>
        )
    }, [
        filters,
        flattenedVisibleColumns,
        leadingColumnCount,
        actionsCell,
        actionsCellClassName,
        form,
        onFilterChange,
    ])

    return (
        <TableWrapper>
            <ColumnManagerWrapper>
                <ColumnManager
                    columnSettings={columnSettings}
                    onToggleColumn={toggleColumn}
                    onShowAll={showAllColumns}
                    onHideAll={hideAllColumns}
                    onReset={resetToDefault}
                />
            </ColumnManagerWrapper>
            <Table columns={visibleColumns} summary={generatedSummary} {...tableProps} />
        </TableWrapper>
    )
}

// Для обратной совместимости - можно использовать как обычную Table
export default TableWithColumnManager

interface LeafColumnDescriptor {
    key?: string | number
}

const flattenLeafColumns = (columns: ColumnsType<any>): LeafColumnDescriptor[] => {
    const result: LeafColumnDescriptor[] = []

    columns.forEach(column => {
        if (!column) {
            return
        }

        const groupColumn = column as ColumnGroupType<any>
        if (groupColumn && Array.isArray(groupColumn.children) && groupColumn.children.length > 0) {
            result.push(...flattenLeafColumns(groupColumn.children))
        } else {
            // Извлекаем ключ столбца
            // В Ant Design ключ может быть string, number или undefined
            const columnKey = column.key
            result.push({
                key: columnKey !== undefined && columnKey !== null ? String(columnKey) : undefined,
            })
        }
    })

    return result
}
